import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, Send, VolumeX, Settings, Loader2, MessageSquare, Phone, Brain, Plus, X, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VOICES = [
  { id: 'anna', name: 'Анна', desc: 'Тёплый', color: '#10B981' },
  { id: 'maria', name: 'Мария', desc: 'Мягкий', color: '#06B6D4' },
  { id: 'alexander', name: 'Александр', desc: 'Уверенный', color: '#8B5CF6' },
  { id: 'dmitry', name: 'Дмитрий', desc: 'Глубокий', color: '#F59E0B' },
];

const PsychologistPage = ({ onFullscreen, onMinimize, isFullscreen }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('anna');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [mode, setMode] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showModeSelection, setShowModeSelection] = useState(true);

  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (mode) {
      const voice = VOICES.find(v => v.id === selectedVoice);
      setMessages([{
        id: 'welcome', role: 'assistant',
        content: `Здравствуйте, я ${voice?.name}. Я здесь, чтобы поговорить с вами. Расскажите, что вас беспокоит.`,
        timestamp: new Date()
      }]);
      setSessionId('');
    }
  }, [selectedVoice, mode]);

  const selectMode = (selectedMode) => {
    setMode(selectedMode);
    setShowModeSelection(false);
  };

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isProcessing) return;

    const userMsg = { id: Date.now().toString(), role: 'user', content: text.trim(), timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsProcessing(true);

    try {
      const formData = new FormData();
      formData.append('message', text.trim());
      formData.append('voice_id', selectedVoice);
      formData.append('session_id', sessionId);

      const res = await fetch(`${API_URL}/api/psychologist/chat`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });

      if (!res.ok) throw new Error('Chat request failed');
      const data = await res.json();

      if (data.session_id) setSessionId(data.session_id);

      const aiMsg = { id: (Date.now() + 1).toString(), role: 'assistant', content: data.text, timestamp: new Date() };
      setMessages(prev => [...prev, aiMsg]);

      if (mode === 'voice') {
        await playTTS(data.text);
      }
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, selectedVoice, sessionId, token, mode, toast]);

  const playTTS = async (text) => {
    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('voice_id', selectedVoice);

      const res = await fetch(`${API_URL}/api/psychologist/tts`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });

      if (!res.ok) return;

      const data = await res.json();
      if (data.audio) {
        setIsPlaying(true);
        const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
        audioRef.current = audio;
        audio.onended = () => setIsPlaying(false);
        audio.onerror = () => setIsPlaying(false);
        await audio.play();
      }
    } catch { }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      toast({ title: 'Ошибка микрофона', description: 'Разрешите доступ к микрофону', variant: 'destructive' });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (blob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');

      const res = await fetch(`${API_URL}/api/psychologist/stt`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });

      if (!res.ok) throw new Error('Transcription failed');
      const data = await res.json();

      if (data.text?.trim()) {
        await sendMessage(data.text);
      } else {
        toast({ title: 'Не удалось распознать речь', variant: 'destructive' });
        setIsProcessing(false);
      }
    } catch (error) {
      toast({ title: 'Ошибка', description: error.message, variant: 'destructive' });
      setIsProcessing(false);
    }
  };

  const createNewChat = () => {
    setMessages([]);
    setSessionId('');
    setShowModeSelection(true);
    setMode(null);
  };

  const currentVoice = VOICES.find(v => v.id === selectedVoice);

  // ==================== MODE SELECTION ====================
  if (showModeSelection) {
    return (
      <div className="flex flex-col h-full bg-transparent" data-testid="mode-selection">
        {/* Header - Liquid Glass */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.03] backdrop-blur-xl border-b border-white/[0.08]">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="liquid-glass-icon w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm sm:text-base leading-tight">AI Психолог</h3>
              <p className="text-white/40 text-[10px] sm:text-xs">Выберите режим общения</p>
            </div>
          </div>
          {/* Fullscreen button - Circular Glass */}
          {onFullscreen && !isFullscreen && (
            <button 
              onClick={onFullscreen} 
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-all hover:scale-105"
              style={{ 
                background: 'rgba(255, 255, 255, 0.12)', 
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              data-testid="psych-fullscreen-btn" 
              title="Развернуть"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
          {isFullscreen && onMinimize && (
            <button 
              onClick={onMinimize} 
              className="liquid-glass-card px-3 sm:px-4 h-9 sm:h-10 flex items-center gap-1.5 text-white/70 hover:text-white text-xs sm:text-sm transition-colors" 
              data-testid="psych-minimize-btn"
            >
              <Minimize2 className="w-4 h-4 relative z-10" /><span className="hidden sm:inline relative z-10">Свернуть</span>
            </button>
          )}
        </div>

        {/* Mode Selection Content */}
        <div className="flex-1 flex items-center justify-center p-3 sm:p-6 overflow-auto">
          <div className="w-full max-w-sm space-y-3 sm:space-y-4">
            {/* Mode Cards - Liquid Glass - Compact */}
            <div className="space-y-2 sm:space-y-3">
              <button
                onClick={() => selectMode('text')}
                className="w-full liquid-glass-card p-3 sm:p-4 flex items-center gap-3 hover:ring-2 hover:ring-emerald-400/30 transition-all"
                data-testid="select-text-mode"
              >
                <div className="relative z-10 liquid-glass-icon w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                </div>
                <div className="relative z-10 text-left">
                  <p className="text-white font-semibold text-sm">Текстовый чат</p>
                  <p className="text-white/50 text-xs mt-0.5">Общение через сообщения</p>
                </div>
              </button>

              <button
                onClick={() => selectMode('voice')}
                className="w-full liquid-glass-card p-3 sm:p-4 flex items-center gap-3 hover:ring-2 hover:ring-purple-400/30 transition-all"
                data-testid="select-voice-mode"
              >
                <div className="relative z-10 liquid-glass-icon w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(139, 92, 246, 0.15)' }}>
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                </div>
                <div className="relative z-10 text-left">
                  <p className="text-white font-semibold text-sm">Голосовой режим</p>
                  <p className="text-white/50 text-xs mt-0.5">Говорите и слушайте ответы</p>
                </div>
              </button>
            </div>

            {/* Voice Selection - Liquid Glass - Compact for mobile */}
            <div className="pt-3 border-t border-white/[0.06]">
              <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider mb-2 text-center font-medium">Выберите голос</p>
              <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                {VOICES.map(voice => (
                  <button
                    key={voice.id}
                    onClick={() => setSelectedVoice(voice.id)}
                    className={`liquid-glass-card flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 ${
                      selectedVoice === voice.id 
                        ? 'ring-2 ring-white/20' 
                        : ''
                    }`}
                    data-testid={`voice-${voice.id}`}
                  >
                    <div 
                      className="relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" 
                      style={{ background: voice.color }}
                    >
                      {voice.name[0]}
                    </div>
                    <div className="relative z-10 text-left min-w-0 flex-1">
                      <span className="text-white text-xs font-medium block truncate">{voice.name}</span>
                      <span className="text-white/40 text-[9px] hidden sm:block">{voice.desc}</span>
                    </div>
                    {selectedVoice === voice.id && <div className="relative z-10 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== MAIN CHAT ====================
  return (
    <div className="flex flex-col h-full bg-transparent" data-testid="psychologist-chat">
      {/* Header - Liquid Glass */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white/[0.03] backdrop-blur-xl border-b border-white/[0.08]">
        <div className="flex items-center gap-2 sm:gap-3">
          <button 
            onClick={() => setShowSettings(!showSettings)} 
            className="liquid-glass-icon w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors" 
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4 sm:w-5 sm:h-5 relative z-10" />
          </button>
          <div>
            <h3 className="text-white font-semibold text-sm leading-tight">AI ПСИХОЛОГ</h3>
            <p className="text-white/40 text-xs">{currentVoice?.name} • {mode === 'voice' ? 'Голос' : 'Текст'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Fullscreen/Minimize button - Circular Glass */}
          {isFullscreen && onMinimize ? (
            <button 
              onClick={onMinimize} 
              className="px-3 sm:px-4 h-9 sm:h-10 rounded-full flex items-center gap-1.5 text-white/70 hover:text-white text-xs sm:text-sm transition-all hover:scale-105" 
              style={{ 
                background: 'rgba(255, 255, 255, 0.12)', 
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              data-testid="psych-minimize-btn"
            >
              <Minimize2 className="w-4 h-4" /><span className="hidden sm:inline">Свернуть</span>
            </button>
          ) : onFullscreen ? (
            <button 
              onClick={onFullscreen} 
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-all hover:scale-105"
              style={{ 
                background: 'rgba(255, 255, 255, 0.12)', 
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              data-testid="psych-fullscreen-btn" 
              title="Развернуть"
            >
              <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          ) : null}
          <button 
            onClick={createNewChat} 
            className="liquid-glass-card px-3 sm:px-4 h-9 sm:h-10 flex items-center gap-1.5 text-white/70 hover:text-white text-xs sm:text-sm transition-colors"
          >
            <Plus className="w-4 h-4 relative z-10" /><span className="hidden sm:inline relative z-10">Новый</span>
          </button>
        </div>
      </div>

      {/* Settings Panel - Liquid Glass - Compact */}
      {showSettings && (
        <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-white/[0.08] bg-white/[0.02] backdrop-blur-xl" data-testid="settings-panel">
          <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-2.5">
            {VOICES.map(voice => (
              <button 
                key={voice.id} 
                onClick={() => setSelectedVoice(voice.id)}
                className={`liquid-glass-card flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 ${
                  selectedVoice === voice.id 
                    ? 'ring-2 ring-white/20' 
                    : ''
                }`}
              >
                <div 
                  className="relative z-10 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-white text-[9px] sm:text-[10px] font-bold flex-shrink-0" 
                  style={{ background: voice.color }}
                >
                  {voice.name[0]}
                </div>
                <span className="relative z-10 text-[10px] sm:text-xs text-white truncate">{voice.name}</span>
                {selectedVoice === voice.id && <div className="relative z-10 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0 ml-auto" />}
              </button>
            ))}
          </div>
          
          {/* Mode Switch - Liquid Glass - Compact */}
          <div className="flex gap-1.5 sm:gap-2 pt-2.5 border-t border-white/[0.06]">
            <button
              onClick={() => setMode('text')}
              className={`flex-1 liquid-glass-card flex items-center justify-center gap-1.5 py-1.5 sm:py-2 text-xs sm:text-sm ${
                mode === 'text' ? 'ring-2 ring-emerald-400/30 text-emerald-400' : 'text-white/60'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 relative z-10" />
              <span className="relative z-10">Текст</span>
            </button>
            <button
              onClick={() => setMode('voice')}
              className={`flex-1 liquid-glass-card flex items-center justify-center gap-1.5 py-1.5 sm:py-2 text-xs sm:text-sm ${
                mode === 'voice' ? 'ring-2 ring-purple-400/30 text-purple-400' : 'text-white/60'
              }`}
            >
              <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 relative z-10" />
              <span className="relative z-10">Голос</span>
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 px-3 sm:px-5">
        <div className="space-y-3 sm:space-y-4 py-4 sm:py-6">
          {messages.length === 0 && (
            <div className="text-center py-12 sm:py-16" data-testid="empty-chat">
              <div className="liquid-glass-icon w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 sm:mb-5" style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3))' }}>
                <Brain className="w-8 h-8 sm:w-10 sm:h-10 text-white relative z-10" />
              </div>
              <p className="text-white text-lg sm:text-xl font-semibold">AI Психолог</p>
              <p className="text-white/40 text-sm mt-2 max-w-sm mx-auto px-4">Начните разговор — я здесь, чтобы помочь</p>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.role}`}>
              <div className={`max-w-[90%] sm:max-w-[75%] liquid-glass-card px-4 sm:px-5 py-3 sm:py-4 ${
                msg.role === 'user' 
                  ? 'ring-1 ring-purple-500/30' 
                  : ''
              }`} style={msg.role === 'user' ? { background: 'rgba(139, 92, 246, 0.15)' } : undefined}>
                {msg.role === 'assistant' && (
                  <div className="relative z-10 flex items-center gap-2 mb-2 sm:mb-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Brain className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                    </div>
                    <span className="text-xs text-white font-semibold">{currentVoice?.name}</span>
                  </div>
                )}
                <p className="relative z-10 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words text-white/90">{msg.content}</p>
                <p className={`relative z-10 text-xs mt-2 ${msg.role === 'user' ? 'text-purple-300/50' : 'text-white/30'}`}>
                  {msg.timestamp.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {/* Typing indicator - Liquid Glass */}
          {isProcessing && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="liquid-glass-card px-4 sm:px-5 py-3 sm:py-4">
                <div className="relative z-10 flex items-center gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Brain className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white animate-pulse" />
                  </div>
                  <span className="text-sm text-white/60 font-medium">Думаю...</span>
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input - Liquid Glass */}
      <div className="p-3 sm:p-4 border-t border-white/[0.08] bg-white/[0.02] backdrop-blur-xl">
        {mode === 'text' ? (
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(inputText); }} className="flex gap-2 sm:gap-3 items-end liquid-glass-card px-2 sm:px-3 py-1.5 sm:py-2 focus-within:ring-2 focus-within:ring-purple-500/30">
            <textarea 
              value={inputText} 
              onChange={(e) => { 
                setInputText(e.target.value); 
                e.target.style.height = '36px'; 
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; 
              }}
              placeholder="Расскажите, что вас беспокоит..."
              className="relative z-10 bg-transparent border-0 text-white placeholder:text-white/40 focus:outline-none focus:ring-0 flex-1 text-sm sm:text-base resize-none py-2 px-2 sm:px-3 overflow-hidden caret-purple-400"
              style={{ height: '36px', minHeight: '36px', maxHeight: '100px' }} 
              disabled={isProcessing} 
              rows={1}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(inputText); }}}
              data-testid="psych-input" 
            />
            <Button 
              type="submit" 
              disabled={!inputText.trim() || isProcessing} 
              className={`relative z-10 rounded-full flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 transition-all ${
                inputText.trim() && !isProcessing 
                  ? 'bg-purple-500 hover:bg-purple-400 text-white' 
                  : 'bg-transparent text-white/30'
              }`} 
              size="icon" 
              data-testid="send-btn"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> : <Send className="w-4 h-4 sm:w-5 sm:h-5" />}
            </Button>
          </form>
        ) : (
          /* Voice Input */
          <div className="flex flex-col items-center gap-3" data-testid="voice-controls">
            <div className="flex items-center gap-4">
              {isPlaying && (
                <Button 
                  onClick={stopAudio} 
                  variant="ghost" 
                  size="icon"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/[0.06] text-white/60 hover:text-white border border-white/[0.08] backdrop-blur-sm" 
                  data-testid="stop-audio-btn"
                >
                  <VolumeX className="w-5 h-5" />
                </Button>
              )}

              <button
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onMouseLeave={() => { if (isRecording) stopRecording(); }}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
                disabled={isProcessing}
                className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
                  isRecording
                    ? 'bg-red-500 scale-110 shadow-lg shadow-red-500/30'
                    : isProcessing
                      ? 'bg-white/10 text-white/30'
                      : 'bg-gradient-to-br from-purple-500 to-pink-500 hover:scale-105 active:scale-95 shadow-lg shadow-purple-500/30'
                } text-white`}
                data-testid="record-btn"
              >
                {isProcessing ? <Loader2 className="w-6 h-6 animate-spin" /> : isRecording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>

              {!isPlaying && !isRecording && !isProcessing && <div className="w-10 sm:w-12" />}
            </div>
            <p className="text-xs text-white/40">
              {isRecording ? 'Отпустите для отправки' : isProcessing ? 'Обработка...' : 'Удерживайте для записи'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PsychologistPage;
